import type { Locale } from '@xphoto/shared';
import { catalogs, resolve, type MessageKey } from './catalogs';

/**
 * PLURIELS i18n (`Intl.PluralRules`).
 *
 * Le projet n'avait aucune gestion des pluriels : deux stratégies coexistaient, la
 * forme parenthésée (`'{count} média(s) restauré(s)'`) et le PLURIEL NU
 * (`'{count} photos à valider (IA)'`), qui rend « 1 photos » — et en allemand
 * « 1 Medien ».
 *
 * POURQUOI `Intl.PluralRules` plutôt que généraliser la forme parenthésée :
 *  - la parenthèse est une convention TYPOGRAPHIQUE FRANÇAISE qui ne se transpose
 *    pas : l'allemand a des pluriels IRRÉGULIERS (Medium → Medien, Datei → Dateien,
 *    Ordner → Ordner). On ne peut pas les former en suffixant « (e) » ;
 *  - les RÈGLES diffèrent selon la langue : en français `0` prend le SINGULIER
 *    (« 0 photo »), en anglais et en allemand le PLURIEL (« 0 photos », « 0 Fotos »).
 *    Aucune forme parenthésée ne peut exprimer cela ; `Intl.PluralRules` le fait
 *    nativement.
 *
 * CONVENTION DE CLÉS : un message qui varie avec un compte fournit `<base>_one` et
 * `<base>_other` dans les TROIS catalogues (la parité de clés existante reste donc
 * un gate). Les catégories intermédiaires (`few`, `many`… — p. ex. le `many`
 * français au-delà du million) retombent sur `_other`.
 */

/**
 * Bases de clés plurielles, DÉRIVÉES du catalogue : toute clé `…_other` en crée une.
 * Une base sans variante `_other` ne compile pas.
 */
export type PluralKey = MessageKey extends infer K
  ? K extends `${infer Base}_other`
    ? Base
    : never
  : never;

/** Cache des instances `Intl.PluralRules` (leur construction n'est pas gratuite). */
const RULES = new Map<Locale, Intl.PluralRules>();

function rulesFor(locale: Locale): Intl.PluralRules {
  let r = RULES.get(locale);
  if (!r) {
    r = new Intl.PluralRules(locale);
    RULES.set(locale, r);
  }
  return r;
}

/**
 * Choisit la variante (`_one`, `_other`…) pour `count` dans `locale`, puis interpole.
 * `{count}` est FORMATÉ selon la langue (12 345 / 12.345 / 12,345), comme le font
 * déjà les autres écrans qui affichent des compteurs.
 */
export function pluralize(
  locale: Locale,
  key: PluralKey,
  count: number,
  vars: Record<string, string | number> = {},
): string {
  const category = rulesFor(locale).select(count);
  const messages = catalogs[locale];
  // `resolve` renvoie la clé brute si absente : on teste donc l'égalité pour
  // détecter le repli plutôt que `undefined`.
  const exact = `${key}_${category}` as MessageKey;
  const fallback = `${key}_other` as MessageKey;
  const resolved = resolve(messages, exact);
  const template = resolved === exact ? resolve(messages, fallback) : resolved;
  return interpolateLocalized(template, locale, { count, ...vars });
}

/**
 * Interpolation `{jeton}` avec FORMATAGE LOCALISÉ des nombres. `gallery/format.ts`
 * faisait un `String(value)` brut — `{count}` rendait `12345` là où les écrans
 * voisins (`SortMenu`, `Toolbar`) affichaient `12 345` pour un compteur comparable.
 */
export function interpolateLocalized(
  template: string,
  locale: Locale,
  vars: Record<string, string | number>,
): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = vars[name];
    if (value === undefined) return match;
    return typeof value === 'number' ? value.toLocaleString(locale) : value;
  });
}
